/**
 * Applies 80% reduction to capacity, then rounds down to the nearest megawatt and converts back to watts.
 *
 * This function is used to ensure capacity values are aligned to whole megawatts,
 * which is often required for bidding and capacity calculations in energy markets.
 * The 80% multiplier is applied before rounding to account for capacity constraints.
 *
 * @param capacityInWatts - The capacity value in watts (W)
 * @returns The adjusted and rounded down capacity in watts (W)
 *
 * @example
 * ```typescript
 * // 1.7 MW * 0.8 = 1.36 MW, rounds down to 1 MW = 1,000,000 W
 * applyCapacityReductionAndRoundDown(1_700_000) // returns 1_000_000
 *
 * // 5 MW * 0.8 = 4 MW, rounds down to 4 MW = 4,000,000 W
 * applyCapacityReductionAndRoundDown(5_000_000) // returns 4_000_000
 *
 * // 10 MW * 0.8 = 8 MW, rounds down to 8 MW = 8,000,000 W
 * applyCapacityReductionAndRoundDown(10_000_000) // returns 8_000_000
 * ```
 */
export function applyCapacityReductionAndRoundDown(capacityInWatts: number): number {
  const reducedCapacity = capacityInWatts * 0.8
  return Math.floor(reducedCapacity / 1_000_000) * 1_000_000
}
